import * as React from 'react';
import styled from 'styled-components';
import { Heading, FloatingActionButton, Mask, icons } from '@invertase/ui';

import Page from '../components/Page';
import Container from '../components/Container';
import Sidebar from '../components/Sidebar';
import NextPrevious from '../components/NextPrevious';
import { MdxNextPrevious } from '../types/mdx';
import { Tag } from '../types/reference';
import Tags from './Tags';
import Header from './Header';
import Link from './Link';

const { Close, ShowSidebar } = icons;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 230px minmax(0, 1fr) auto;

  @media (max-width: 1023px) {
    display: flex;
  }
`;

interface Props {
  location: Location;
  title: string;
  excerpt?: string;
  description?: string | React.ReactElement;
  children: React.ReactElement | React.ReactElement[];
  aside?: React.ReactElement | React.ReactElement[] | null;
  next?: MdxNextPrevious;
  previous?: MdxNextPrevious;
  tags?: Tag[];
}

function DocumentationLayout({
  location,
  title,
  excerpt,
  description,
  children,
  aside,
  previous,
  next,
  tags,
}: Props) {
  const [showSidebar, setShowSidebar] = React.useState<boolean>(false);

  return (
    <Page
      location={location}
      title={title}
      description={typeof description === 'string' ? description : excerpt}
      header={<Header />}
    >
      <Container>
        <Grid className="pb-10 lg:pb-0">
          <div className="hidden lg:block">
            <Sidebar />
          </div>
          <article className="pt-10 lg:pt-20 lg:pl-10 pb-16 max-w-full">
            {!!title && (
              <>
                <div className="lg:flex items-center">
                  <Heading size="h1" as="h2" className="flex-1" style={{ marginTop: '0px' }}>
                    {title}
                  </Heading>
                  {!!tags && <Tags tags={tags} />}
                </div>
                {!!description && (
                  <div className="mt-2">
                    <span className="font-thin">{description}</span>
                  </div>
                )}
                <NextPrevious next={next} previous={previous} />
                <hr className="mt-6" />
              </>
            )}
            {children}
            <hr className="mt-10" />
            <NextPrevious next={next} previous={previous} />
            <div className="mt-8 flex items-center text-xs text-gray-500">
              <div className="flex flex-1">
                <div>Copyright 2020 - 2021 &copy; Invertase Limited</div>
              </div>
              <ul className="flex">
                <li className="ml-4">
                  <Link to="https://github.com/invertase/notifee" target="_blank">
                    GitHub
                  </Link>
                </li>
                <li className="ml-4">
                  <Link to="/contact" target="_blank">
                    Contact
                  </Link>
                </li>
                <li className="ml-4">
                  <Link to="/frequently-asked-questions" target="_blank">
                    Frequently Asked Questions
                  </Link>
                </li>
              </ul>
            </div>
            <div className="mt-16 text-xs text-gray-500">
              <p>
                Except as otherwise noted, the content of this page is licensed under the{' '}
                <Link to="https://creativecommons.org/licenses/by/3.0/">
                  Creative Commons Attribution 3.0 License
                </Link>{' '}
                and code samples are licensed under the{' '}
                <Link to="https://www.apache.org/licenses/LICENSE-2.0">Apache 2.0 License</Link>.
              </p>
              <p>
                All product names, logos, and brands are property of their respective owners. All
                company, product and service names used in this website are for identification
                purposes only. Use of these names, logos, and brands does not imply endorsement.
              </p>
            </div>
          </article>
          {aside && (
            <div className="hidden lg:block" style={{ width: 200 }}>
              {aside}
            </div>
          )}
        </Grid>

        {/* Dark background overlay */}
        <Mask visible={showSidebar} onClose={() => setShowSidebar(false)} className="mt-16">
          <div className="mt-16 pl-2 border-t dark:border-gray-700 transition-400 lg:hidden fixed w-3/4 md:w-1/2 inset-0 z-40 bg-white dark:bg-gray-800 shadow-lg">
            <Sidebar />
          </div>
        </Mask>

        {/* FAB to toggle sidebar menu */}
        <FloatingActionButton
          className="lg:hidden"
          onClick={() => setShowSidebar($ => !$)}
          icon={!showSidebar ? <ShowSidebar /> : <Close />}
        />
      </Container>
    </Page>
  );
}

export default DocumentationLayout;
